import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { supabase } from '../../data/supabaseClient';
import { 
  FaBox, 
  FaTruck, 
  FaHandshake, 
  FaCheckCircle, 
  FaTimes, 
  FaCaretDown, 
  FaClock, 
  FaExclamationTriangle, 
  FaUsers, 
  FaMapMarkerAlt,
  FaRegStar,
  FaRegHandshake
} from "react-icons/fa";
import { CustomButton10, CustomButton10Ca, CustomButton10Co } from '../../Templates';
import { useToast } from '../../hooks/ToastContext';
import { useTranslation } from 'react-i18next';
import Spinner from '../../common/Spinner';
import { checkAndMigratePackageDeliveryColumns } from '../../../utils/packageDeliveryMigration';
import './PackageActionsV2.css';

// Import V2 Phase Components
import PickupMeetingAgreement from './PickupMeetingAgreement';
import PickupArrivalConfirmation from './PickupArrivalConfirmation';
import PickupWaitingStatus from './PickupWaitingStatus';
import DeliveryMeetingAgreement from './DeliveryMeetingAgreement';
import DeliveryArrivalConfirmation from './DeliveryArrivalConfirmation';
import DeliveryWaitingStatus from './DeliveryWaitingStatus';
import DeliveryCountdownStatus from './DeliveryCountdownStatus';
import PackageStatusDisplay from './PackageStatusDisplay';

// V2 Status Constants
const PackageStatusV2 = {
  // Initial flow
  REQUEST_SENT: 'request_sent',
  CONFIRMED: 'confirmed',
  
  // Pickup phases
  PICKUP_MEETING_AGREEMENT: 'pickup_meeting_agreement',
  PICKUP_MEETING_AGREED: 'pickup_meeting_agreed',
  PICKUP_ARRIVAL_PHASE: 'pickup_arrival_phase',
  PICKUP_WAITING: 'pickup_waiting',
  PICKUP_BOTH_PRESENT: 'pickup_both_present',
  PACKAGE_PICKED_UP: 'package_picked_up',
  
  // Delivery phases
  DELIVERY_MEETING_AGREEMENT: 'delivery_meeting_agreement',
  DELIVERY_MEETING_AGREED: 'delivery_meeting_agreed',
  DELIVERY_ARRIVAL_PHASE: 'delivery_arrival_phase',
  DELIVERY_WAITING: 'delivery_waiting',
  DELIVERY_BOTH_PRESENT: 'delivery_both_present',
  PACKAGE_DELIVERED: 'package_delivered',
  
  // Completion
  DELIVERY_CONFIRMED: 'delivery_confirmed',
  COMPLETED: 'completed',
  
  // Dispute & Return states
  DELIVERY_DISPUTED: 'delivery_disputed',
  WAITING_REVIEW: 'waiting_review',
  RETURN_REQUESTED: 'return_requested',
  
  // Error states
  PICKUP_SENDER_NO_SHOW: 'pickup_sender_no_show',
  PICKUP_CARRIER_NO_SHOW: 'pickup_carrier_no_show',
  DELIVERY_CARRIER_NO_SHOW: 'delivery_carrier_no_show',
  DELIVERY_RECIPIENT_NO_SHOW: 'delivery_recipient_no_show',
  PICKUP_DISPUTED: 'pickup_disputed',
  CANCELLED: 'cancelled',
  DECLINED: 'declined'
};

const PackageActionsV2 = ({ conversationId, packageId, onUpdate }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const { t } = useTranslation('common');
  const { addToast } = useToast();
  
  // Core state
  const [currentUser, setCurrentUser] = useState(null);
  const [packageData, setPackageData] = useState(null);
  const [delivery, setDelivery] = useState(null);
  const [otherUser, setOtherUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // V2 specific state
  const [waitingDuration, setWaitingDuration] = useState(3); // Default 3 minutes
  const [subscriptionActive, setSubscriptionActive] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(Date.now());
  const [manualRefreshing, setManualRefreshing] = useState(false);
  
  // Review state
  const [senderReviewSubmitted, setSenderReviewSubmitted] = useState(false);
  const [carrierReviewSubmitted, setCarrierReviewSubmitted] = useState(false);
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  
  // Simplified toggle handler
  const handleToggleDropdown = (event) => {
    event.preventDefault();
    event.stopPropagation();
    
    setDropdownOpen(prev => !prev);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Only handle if dropdown is open
      if (!dropdownOpen) return;
      
      // Check if any modal is open
      const modalExists = document.querySelector('.PAC-modal-overlay-v2, .PAC-modal-overlay, .modal-overlay, .review-dialog-container');
      if (modalExists) return;
      
      // Check if click is outside dropdown AND not on the toggle button
      if (dropdownRef.current && 
          !dropdownRef.current.contains(event.target) &&
          !event.target.closest('.PAC-package-actions-button')) {
        setDropdownOpen(false);
      }
    };
    
    // Use only mousedown to avoid event conflicts
    document.addEventListener('mousedown', handleClickOutside);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen]); // Add dropdownOpen as dependency

  // Helper function to fetch delivery data with retry logic
  const fetchDeliveryData = async (retries = 3) => {
    if (!packageId || !conversationId || !currentUser) return null;
    
    for (let attempt = 1; attempt <= retries; attempt++) {
      try {
        console.log(`Fetching delivery data (attempt ${attempt}/${retries})`);
        
        const { data: deliveryData, error: deliveryError } = await supabase
          .from('package_deliveries')
          .select('*')
          .eq('package_id', packageId)
          .eq('conversation_id', conversationId)
          .not('status', 'eq', 'cancelled')
          .not('status', 'eq', 'declined')
          .maybeSingle();

        if (deliveryError && deliveryError.code !== 'PGRST116') {
          throw deliveryError;
        }
        
        console.log('Delivery data fetched successfully:', deliveryData);
        return deliveryData;
        
      } catch (error) {
        console.error(`Error fetching delivery data (attempt ${attempt}):`, error);
        
        if (attempt < retries) {
          // Wait before retrying (exponential backoff)
          const delay = Math.pow(2, attempt) * 1000; // 2s, 4s, 8s
          console.log(`Retrying in ${delay}ms...`);
          await new Promise(resolve => setTimeout(resolve, delay));
        } else {
          console.error('All retry attempts failed for fetchDeliveryData');
          return null;
        }
      }
    }
    
    return null;
  };

  // Initialize data and subscriptions with retry logic
  useEffect(() => {
    const initializeData = async (attempt = 1) => {
      if (!packageId || !conversationId) {
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        setError(null);
        
        console.log(`Initializing package actions V2 data (attempt ${attempt})...`);
        
        // Check database columns first
        const dbCheck = await checkAndMigratePackageDeliveryColumns();
        if (!dbCheck.success) {
          console.warn('Database columns check failed:', dbCheck.error);
        }
        
        // Get current user
        const { data: { user }, error: userError } = await supabase.auth.getUser();
        if (userError) throw userError;
        setCurrentUser(user);

        // Get package details with retry
        let pkgData = null;
        let pkgError = null;
        
        for (let retryAttempt = 1; retryAttempt <= 2; retryAttempt++) {
          const { data, error } = await supabase
            .from('packages')
            .select('*')
            .eq('id', packageId)
            .single();
          
          if (!error) {
            pkgData = data;
            break;
          }
          
          pkgError = error;
          console.error(`Attempt ${retryAttempt} - Error fetching package:`, error);
          
          if (retryAttempt < 2) {
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        }
        
        if (pkgError && !pkgData) {
          console.error('Failed to fetch package after retries:', pkgError);
          // Don't throw - continue with limited functionality
        }
        
        setPackageData(pkgData);

        // Get existing package delivery for this specific conversation
        const deliveryData = await fetchDeliveryData(2); // Reduced retries for initial load
        console.log('Package delivery data fetched:', deliveryData);
        setDelivery(deliveryData || null);

        // Get other user details if delivery exists
        if (deliveryData && user) {
          const otherUserId = user.id === deliveryData.sender_id ? 
            deliveryData.carrier_id : deliveryData.sender_id;
          
          const { data: otherUserData, error: otherUserError } = await supabase
            .from('profiles')
            .select('id, username, avatar_url')
            .eq('id', otherUserId)
            .single();
          
          if (otherUserError) {
            console.error('Error fetching other user:', otherUserError);
          } else {
            setOtherUser(otherUserData);
          }
            
          // If delivery is completed, always check review status
          if (deliveryData.status === PackageStatusV2.COMPLETED) {
            console.log('Delivery is completed, loading review status...');
            await loadReviewStatus(deliveryData);
          }
        }
        
        console.log('Package actions V2 initialization complete');

      } catch (error) {
        console.error(`Error initializing V2 package data (attempt ${attempt}):`, error);
        
        if (attempt < 3 && (error.message?.includes('network') || error.message?.includes('fetch'))) {
          console.log(`Retrying initialization in 2 seconds... (${attempt}/3)`);
          setTimeout(() => initializeData(attempt + 1), 2000);
          return;
        }
        
        setError(t('package_actions.error_loading'));
      } finally {
        setLoading(false);
      }
    };

    initializeData();
  }, [packageId, conversationId, t]);

  // Combined effect for data initialization and real-time subscriptions
  useEffect(() => {
    if (!packageId || !conversationId || !currentUser) return;

    console.log('Setting up real-time subscriptions for conversation:', conversationId);

    // Set up real-time subscription for package_deliveries table
    const channel = supabase
      .channel(`package-delivery-v2-${conversationId}-${packageId}`)
      .on('postgres_changes', 
        { 
          event: '*', // Listen for all events (INSERT, UPDATE, DELETE)
          schema: 'public', 
          table: 'package_deliveries',
          filter: `conversation_id=eq.${conversationId}`
        },
        async (payload) => {
          console.log('📦 V2 Package delivery real-time event:', payload.eventType, payload);
          
          // Handle different event types
          switch (payload.eventType) {
            case 'INSERT':
              if (payload.new && payload.new.package_id === packageId) {
                console.log('New delivery created for this package');
                setDelivery(payload.new);
                
                // Fetch other user details
                const otherUserId = currentUser.id === payload.new.sender_id ? 
                  payload.new.carrier_id : payload.new.sender_id;
                
                try {
                  const { data: otherUserData } = await supabase
                    .from('profiles')
                    .select('id, username, avatar_url')
                    .eq('id', otherUserId)
                    .single();
                  
                  if (otherUserData) {
                    setOtherUser(otherUserData);
                  }
                } catch (error) {
                  console.error('Error fetching other user in real-time:', error);
                }
              }
              break;
              
            case 'UPDATE':
              if (payload.new && payload.new.package_id === packageId) {
                console.log('Delivery updated for this package');
                setDelivery(payload.new);
                
                // If status changed to completed, check reviews
                if (payload.new.status === PackageStatusV2.COMPLETED) {
                  console.log('Delivery status changed to COMPLETED, loading review status...');
                  await loadReviewStatus(payload.new);
                }
              }
              break;
              
            case 'DELETE':
              if (payload.old && payload.old.package_id === packageId) {
                console.log('Delivery deleted for this package');
                setDelivery(null);
                setOtherUser(null);
              }
              break;
          }
        }
      )
      .subscribe((status) => {
        console.log('Subscription status:', status);
        if (status === 'SUBSCRIBED') {
          console.log('Successfully subscribed to package delivery updates');
          setSubscriptionActive(true);
          
          // After subscription is established, do a final data refresh
          // This ensures we catch any data that was created during subscription setup
          setTimeout(async () => {
            try {
              const deliveryData = await fetchDeliveryData(1); // Single attempt for post-subscription check
              if (deliveryData && deliveryData.id !== delivery?.id) {
                console.log('Found new delivery data after subscription setup:', deliveryData);
                setDelivery(deliveryData);
                
                // Fetch other user if needed
                if (!otherUser) {
                  const otherUserId = currentUser.id === deliveryData.sender_id ? 
                    deliveryData.carrier_id : deliveryData.sender_id;
                  
                  const { data: otherUserData } = await supabase
                    .from('profiles')
                    .select('id, username, avatar_url')
                    .eq('id', otherUserId)
                    .single();
                  
                  if (otherUserData) {
                    setOtherUser(otherUserData);
                  }
                }
              }
            } catch (error) {
              console.error('Error in post-subscription data refresh:', error);
            }
          }, 500); // Reduced delay to 500ms for faster response
          
          // Also do an immediate check in case data is already available
          setTimeout(async () => {
            try {
              const deliveryData = await fetchDeliveryData(1);
              if (deliveryData && !delivery) {
                console.log('Immediate delivery data found after subscription:', deliveryData);
                setDelivery(deliveryData);
                
                // Fetch other user if needed
                const otherUserId = currentUser.id === deliveryData.sender_id ? 
                  deliveryData.carrier_id : deliveryData.sender_id;
                
                const { data: otherUserData } = await supabase
                  .from('profiles')
                  .select('id, username, avatar_url')
                  .eq('id', otherUserId)
                  .single();
                
                if (otherUserData) {
                  setOtherUser(otherUserData);
                }
              }
            } catch (error) {
              console.error('Error in immediate post-subscription check:', error);
            }
          }, 100); // Very quick check
        }
      });

    return () => {
      console.log('Cleaning up real-time subscription');
      setSubscriptionActive(false);
      supabase.removeChannel(channel);
    };
  }, [packageId, conversationId, currentUser]); // Removed delivery?.id to prevent subscription restarts

  // Fallback polling mechanism in case real-time updates fail
  useEffect(() => {
    if (!subscriptionActive || !packageId || !conversationId || !currentUser) return;
    
    const pollInterval = setInterval(async () => {
      try {
        const deliveryData = await fetchDeliveryData(1);
        if (deliveryData && deliveryData.id !== delivery?.id) {
          console.log('Fallback polling found new delivery data:', deliveryData);
          setDelivery(deliveryData);
          
          // Fetch other user if needed
          if (!otherUser) {
            const otherUserId = currentUser.id === deliveryData.sender_id ? 
              deliveryData.carrier_id : deliveryData.sender_id;
            
            const { data: otherUserData } = await supabase
              .from('profiles')
              .select('id, username, avatar_url')
              .eq('id', otherUserId)
              .single();
            
            if (otherUserData) {
              setOtherUser(otherUserData);
            }
          }
        }
      } catch (error) {
        console.error('Error in fallback polling:', error);
      }
    }, 5000); // Poll every 5 seconds
    
    return () => {
      clearInterval(pollInterval);
    };
  }, [subscriptionActive, packageId, conversationId, currentUser, delivery?.id, otherUser]);

  // Check for dispute timeouts
  useEffect(() => {
    if (!delivery || delivery.status !== PackageStatusV2.DELIVERY_DISPUTED) return;
    
    const timeoutInterval = setInterval(checkDisputeTimeout, 60000); // Check every minute
    
    // Also check immediately
    checkDisputeTimeout();
    
    return () => clearInterval(timeoutInterval);
  }, [delivery?.id, delivery?.status, delivery?.dispute_response_deadline]);

  // Manual refresh function
  const handleManualRefresh = async () => {
    if (manualRefreshing) return;
    
    setManualRefreshing(true);
    try {
      console.log('Manual refresh triggered');
      const deliveryData = await fetchDeliveryData(1);
      if (deliveryData) {
        setDelivery(deliveryData);
        setLastRefresh(Date.now());
        
        // Fetch other user if needed
        if (!otherUser && deliveryData) {
          const otherUserId = currentUser.id === deliveryData.sender_id ? 
            deliveryData.carrier_id : deliveryData.sender_id;
          
          const { data: otherUserData } = await supabase
            .from('profiles')
            .select('id, username, avatar_url')
            .eq('id', otherUserId)
            .single();
          
          if (otherUserData) {
            setOtherUser(otherUserData);
          }
        }
      }
    } catch (error) {
      console.error('Error in manual refresh:', error);
    } finally {
      setManualRefreshing(false);
    }
  };

  // Re-check review status when delivery status changes to completed
  useEffect(() => {
    if (delivery?.status === PackageStatusV2.COMPLETED && delivery?.id) {
      console.log('Delivery is completed, checking review status...');
      loadReviewStatus(delivery);
    }
  }, [delivery?.status, delivery?.id]);

  // Load review status for completed deliveries
  const loadReviewStatus = async (deliveryData) => {
    try {
      if (!deliveryData?.id || !deliveryData?.sender_id || !deliveryData?.carrier_id) {
        console.log('Missing required data for loading review status');
        return;
      }

      console.log('Loading review status for delivery:', deliveryData.id);
      
      // Check if sender has reviewed
      const { data: senderReview } = await supabase
        .from('package_reviews')
        .select('id')
        .eq('delivery_id', deliveryData.id)
        .eq('reviewer_id', deliveryData.sender_id)
        .maybeSingle();
      
      setSenderReviewSubmitted(!!senderReview);
      if (senderReview) {
        console.log('✅ Sender has already reviewed');
      }

      // Check if carrier has reviewed
      const { data: carrierReview } = await supabase
        .from('package_reviews')
        .select('id')
        .eq('delivery_id', deliveryData.id)
        .eq('reviewer_id', deliveryData.carrier_id)
        .maybeSingle();
      
      setCarrierReviewSubmitted(!!carrierReview);
      if (carrierReview) {
        console.log('✅ Carrier has already reviewed');
      }

      console.log('Review status loaded - Sender:', !!senderReview, 'Carrier:', !!carrierReview);
      
      setSenderReviewSubmitted(!!senderReview);
      setCarrierReviewSubmitted(!!carrierReview);
    } catch (error) {
      console.error('Error loading review status:', error);
      // Don't let review loading errors break the component
      setSenderReviewSubmitted(false);
      setCarrierReviewSubmitted(false);
    }
  };

  // Check if a review already exists
  const checkExistingReview = async (deliveryId, reviewerId) => {
    try {
      console.log('🔍 Checking existing review for:', { deliveryId, reviewerId });
      
      if (!deliveryId || !reviewerId) {
        console.log('Missing deliveryId or reviewerId');
        return false;
      }
      
      const { data, error } = await supabase
        .from('package_reviews')
        .select('id')
        .eq('delivery_id', deliveryId)
        .eq('reviewer_id', reviewerId)
        .single();
  
      if (error) {
        if (error.code === 'PGRST116') {
          // No review found
          console.log('📝 No existing review found');
          return false;
        }
        console.error('Error checking package review:', error);
        return false;
      }
      
      console.log('📝 Existing review found:', !!data);
      return !!data;
    } catch (error) {
      console.error('Error in checkExistingReview:', error);
      return false;
    }
  };

  // Create wrapper function that matches the old signature for child components
  const createNotification = async (userId, type, message, relatedId = null) => {
    // Note: message parameter is ignored - we use database translation instead
    try {
      const { error: notificationError } = await supabase.rpc('create_translated_notification', {
        target_user_id: userId,
        notification_type: type,
        sender_id: currentUser.id,
        conversation_id: conversationId,
        related_id: relatedId ? relatedId.toString() : null
      });
      
      if (notificationError) {
        console.error('Failed to create translated notification:', notificationError);
        throw notificationError;
      }
    } catch (error) {
      console.error('Error creating translated notification:', error);
      throw error;
    }
  };

  // Function to add system message about package actions
  const addSystemMessage = async (messageText) => {
    try {
      if (!conversationId || !messageText) return;
      
      const { data: { user: currentUser }, error: userError } = await supabase.auth.getUser();
      
      if (userError) {
        console.error('Error getting current user for system message:', userError);
        return;
      }
      
      if (!currentUser?.id) {
        console.error('No current user ID available for system message');
        return;
      }
      
      await supabase
        .from('messages')
        .insert({
          conversation_id: conversationId,
          user_id: currentUser.id,
          text: messageText,
          created_at: new Date().toISOString(),
          is_system_message: true
        });
      
      await supabase
        .from('conversations')
        .update({
          last_message: messageText,
          last_updated: new Date().toISOString()
        })
        .eq('id', conversationId);
        
    } catch (error) {
      console.error('Error adding system message:', error);
    }
  };



  // Handle initial package delivery request
  const handleRequestDelivery = async () => {
    setDropdownOpen(false);
    if (actionLoading) return;
    
    try {
      setActionLoading(true);
      setError(null);
      
      if (!currentUser || !packageData) {
        throw new Error(t('package_actions.error_user_or_package'));
      }

      // Determine roles correctly
      const senderId = packageData.user_id;
      const carrierId = currentUser.id;
      
      // Create the delivery with V2 initial status
      const { data: newDelivery, error: deliveryError } = await supabase
        .from('package_deliveries')
        .insert([{
          package_id: packageId,
          sender_id: senderId,
          carrier_id: carrierId,
          conversation_id: conversationId, // Link to specific conversation
          status: PackageStatusV2.REQUEST_SENT,
          created_at: new Date().toISOString(),
          // V2 specific fields
          meeting_duration_minutes: waitingDuration,
          v2_enabled: true // Flag to identify V2 deliveries
        }])
        .select()
        .single();
  
      if (deliveryError) throw deliveryError;
      
      // Immediately update local state to ensure UI responsiveness
      setDelivery(newDelivery);
      setLastRefresh(Date.now());
  
      // Create notification using database translation
      try {
        const { error: notificationError } = await supabase.rpc('create_translated_notification', {
          target_user_id: packageData.user_id,
          notification_type: 'package_request',
          sender_id: currentUser.id,
          conversation_id: conversationId,
          related_id: newDelivery.id.toString()
        });
        
        if (notificationError) {
          console.error('Failed to create package request notification:', notificationError);
        }
      } catch (notificationError) {
        console.error('Error creating package request notification:', notificationError);
      }
      
      // Add system message
      await addSystemMessage(t('package_actions.delivery_request_sent_system'));
  
      addToast({
        type: 'success',
        title: t('package_actions.success'),
        description: t('package_actions.delivery_request_sent')
      });
  
    } catch (error) {
      console.error('Error in handleRequestDelivery:', error);
      setError(t('package_actions.error_request'));
      
      addToast({
        type: 'error',
        title: t('package_actions.error'),
        description: error.message || t('package_actions.error_request')
      });
    } finally {
      setActionLoading(false);
    }
  };

  // Handle accepting/declining delivery requests
  const handleRequestResponse = async (accept) => {
    setDropdownOpen(false);
    if (actionLoading) return;
    
    try {
      setActionLoading(true);
      setError(null);
      
      if (!delivery?.id) {
        throw new Error(t('package_actions.error_no_delivery'));
      }

      // Update delivery status
      const newStatus = accept ? PackageStatusV2.CONFIRMED : PackageStatusV2.DECLINED;
      const { data: updatedDelivery, error: deliveryError } = await supabase
        .from('package_deliveries')
        .update({ 
          status: newStatus,
          updated_at: new Date().toISOString()
        })
        .eq('id', delivery.id)
        .select()
        .single();

      if (deliveryError) throw deliveryError;

      // Update state immediately
      if (updatedDelivery) {
        setDelivery(updatedDelivery);
        setLastRefresh(Date.now());
      }

      // Get recipient ID
      const recipientId = currentUser.id === delivery.sender_id ? 
        delivery.carrier_id : delivery.sender_id;

      // Create notification using database translation
      try {
        const { error: notificationError } = await supabase.rpc('create_translated_notification', {
          target_user_id: recipientId,
          notification_type: accept ? 'package_accepted' : 'package_declined',
          sender_id: currentUser.id,
          conversation_id: conversationId,
          related_id: delivery.id.toString()
        });
        
        if (notificationError) {
          console.error('Failed to create package response notification:', notificationError);
        }
      } catch (notificationError) {
        console.error('Error creating package response notification:', notificationError);
      }
      
      // Add system message
      await addSystemMessage(accept ? 
        t('package_actions.delivery_accepted_system') : 
        t('package_actions.delivery_declined_system'));

      addToast({
        type: accept ? 'success' : 'info',
        title: accept ? t('package_actions.accepted') : t('package_actions.declined'),
        description: accept ? 
          t('package_actions.delivery_was_accepted') : 
          t('package_actions.delivery_was_declined')
      });

    } catch (error) {
      console.error('Error responding to delivery request:', error);
      setError(t('package_actions.error_response'));
      
      addToast({
        type: 'error',
        title: t('package_actions.error'),
        description: error.message || t('package_actions.error_response')
      });
    } finally {
      setActionLoading(false);
    }
  };

  // Handle delivery confirmation (final step)
  const handleDeliveryConfirmation = async () => {
    setDropdownOpen(false);
    if (actionLoading) return;
    
    try {
      setActionLoading(true);
      setError(null);
      
      if (!delivery?.id) {
        throw new Error(t('package_actions.error_no_delivery'));
      }

      // Update delivery status to completed
      const { data: updatedDelivery, error: deliveryError } = await supabase
        .from('package_deliveries')
        .update({ 
          status: PackageStatusV2.COMPLETED,
          completed_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', delivery.id)
        .select()
        .single();

      if (deliveryError) throw deliveryError;

      // Update state immediately
      if (updatedDelivery) {
        setDelivery(updatedDelivery);
        setLastRefresh(Date.now());
      }

      // Stats are now automatically updated by database trigger with proper permissions
      console.log('Package delivery completed - stats will be updated automatically by trigger');

      // Load review status immediately after completion
      await loadReviewStatus(updatedDelivery);

      // Create notification for carrier using database translation
      try {
        const { error: notificationError } = await supabase.rpc('create_translated_notification', {
          target_user_id: delivery.carrier_id,
          notification_type: 'package_completed',
          sender_id: currentUser.id,
          conversation_id: conversationId,
          related_id: delivery.id.toString()
        });
        
        if (notificationError) {
          console.error('Failed to create package completion notification:', notificationError);
        }
      } catch (notificationError) {
        console.error('Error creating package completion notification:', notificationError);
      }
      
      // Add system message
      await addSystemMessage(t('package_actions.delivery_completed_system'));

      addToast({
        type: 'success',
        title: t('package_actions.delivery_completed'),
        description: t('package_actions.delivery_completed_description')
      });

    } catch (error) {
      console.error('Error confirming delivery:', error);
      setError(t('package_actions.error_confirmation'));
      
      addToast({
        type: 'error',
        title: t('package_actions.error'),
        description: error.message || t('package_actions.error_confirmation')
      });
    } finally {
      setActionLoading(false);
    }
  };

  // Handle when sender reports package not received
  const handlePackageNotReceived = async () => {
    setDropdownOpen(false);
    if (actionLoading) return;
    
    try {
      setActionLoading(true);
      setError(null);
      
      if (!delivery?.id) {
        throw new Error(t('package_actions.error_no_delivery'));
      }

      // Calculate 2-hour deadline
      const deadline = new Date();
      deadline.setHours(deadline.getHours() + 2);

      const { data: updatedDelivery, error: deliveryError } = await supabase
        .from('package_deliveries')
        .update({ 
          status: PackageStatusV2.DELIVERY_DISPUTED,
          dispute_response_deadline: deadline.toISOString(),
          delivery_disputed: true,
          delivery_dispute_details: {
            initiated_by: currentUser.id,
            initiated_at: new Date().toISOString(),
            reason: 'package_not_received',
            deadline: deadline.toISOString()
          },
          updated_at: new Date().toISOString()
        })
        .eq('id', delivery.id)
        .select()
        .single();

      if (deliveryError) throw deliveryError;

      setDelivery(updatedDelivery);
      setLastRefresh(Date.now());

      // Notify carrier about dispute using database translation
      try {
        const { error: notificationError } = await supabase.rpc('create_translated_notification', {
          target_user_id: delivery.carrier_id,
          notification_type: 'delivery_disputed',
          sender_id: currentUser.id,
          conversation_id: conversationId,
          related_id: delivery.id.toString()
        });
        
        if (notificationError) {
          console.error('Failed to create delivery dispute notification:', notificationError);
        }
      } catch (notificationError) {
        console.error('Error creating delivery dispute notification:', notificationError);
      }
      
      await addSystemMessage(t('package_actions.delivery_disputed_system_message'));

      addToast({
        type: 'info',
        title: t('package_actions.dispute_initiated'),
        description: t('package_actions.carrier_has_2_hours')
      });

    } catch (error) {
      console.error('Error reporting package not received:', error);
      setError(t('package_actions.error_dispute'));
      
      addToast({
        type: 'error',
        title: t('package_actions.error'),
        description: error.message || t('package_actions.error_dispute')
      });
    } finally {
      setActionLoading(false);
    }
  };

  // Handle carrier response to dispute
  const handleDisputeResponse = async (wasDelivered) => {
    setDropdownOpen(false);
    if (actionLoading) return;
    
    try {
      setActionLoading(true);
      setError(null);
      
      if (!delivery?.id) {
        throw new Error(t('package_actions.error_no_delivery'));
      }

      let newStatus;
      let systemMessage;
      let notificationMessage;

      if (wasDelivered) {
        // Carrier insists package was delivered - escalate to admin review
        newStatus = PackageStatusV2.WAITING_REVIEW;
        systemMessage = t('package_actions.dispute_escalated_system');
        notificationMessage = t('package_actions.dispute_escalated_notification');
      } else {
        // Carrier admits non-delivery - initiate return process
        newStatus = PackageStatusV2.RETURN_REQUESTED;
        systemMessage = t('package_actions.return_initiated_system');
        notificationMessage = t('package_actions.return_initiated_notification');
      }

      const { data: updatedDelivery, error: deliveryError } = await supabase
        .from('package_deliveries')
        .update({ 
          status: newStatus,
          dispute_response_deadline: null, // Clear deadline
          delivery_dispute_details: {
            ...delivery.delivery_dispute_details,
            carrier_response: wasDelivered ? 'delivered' : 'not_delivered',
            carrier_response_at: new Date().toISOString(),
            escalated_to_review: wasDelivered
          },
          updated_at: new Date().toISOString()
        })
        .eq('id', delivery.id)
        .select()
        .single();

      if (deliveryError) throw deliveryError;

      setDelivery(updatedDelivery);
      setLastRefresh(Date.now());

      // Notify sender using database translation
      try {
        const { error: notificationError } = await supabase.rpc('create_translated_notification', {
          target_user_id: delivery.sender_id,
          notification_type: wasDelivered ? 'dispute_escalated' : 'return_requested',
          sender_id: currentUser.id,
          conversation_id: conversationId,
          related_id: delivery.id.toString()
        });
        
        if (notificationError) {
          console.error('Failed to create dispute response notification:', notificationError);
        }
      } catch (notificationError) {
        console.error('Error creating dispute response notification:', notificationError);
      }
      
      await addSystemMessage(systemMessage);

      addToast({
        type: wasDelivered ? 'info' : 'success',
        title: wasDelivered ? t('package_actions.escalated_for_review') : t('package_actions.return_initiated'),
        description: wasDelivered ? 
          t('package_actions.admin_will_review') : 
          t('package_actions.coordinate_return')
      });

    } catch (error) {
      console.error('Error responding to dispute:', error);
      setError(t('package_actions.error_dispute_response'));
      
      addToast({
        type: 'error',
        title: t('package_actions.error'),
        description: error.message || t('package_actions.error_dispute_response')
      });
    } finally {
      setActionLoading(false);
    }
  };

  // Handle return confirmation
  const handleReturnConfirmation = async () => {
    if (actionLoading) return;
    
    try {
      setActionLoading(true);
      setError(null);
      
      if (!delivery?.id) {
        throw new Error(t('package_actions.error_no_delivery'));
      }

      // Update user's return confirmation status
      const isCarrier = currentUser.id === delivery.carrier_id;
      const updateField = isCarrier ? 'return_confirmed_by_carrier' : 'return_confirmed_by_sender';
      
      const { data: updatedDelivery, error: updateError } = await supabase
        .from('package_deliveries')
        .update({
          [updateField]: true,
          [`${updateField}_at`]: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', delivery.id)
        .select()
        .single();

      if (updateError) throw updateError;

      // Check if both parties have confirmed return
      if (updatedDelivery.return_confirmed_by_carrier && updatedDelivery.return_confirmed_by_sender) {
        // Both confirmed - cancel the delivery
        const { data: cancelledDelivery, error: cancelError } = await supabase
          .from('package_deliveries')
          .update({
            status: PackageStatusV2.CANCELLED,
            cancelled_at: new Date().toISOString(),
            cancellation_reason: 'package_returned',
            updated_at: new Date().toISOString()
          })
          .eq('id', delivery.id)
          .select()
          .single();

        if (cancelError) throw cancelError;

        setDelivery(cancelledDelivery);
        await addSystemMessage(t('package_actions.package_returned_successfully'));
        
        addToast({
          type: 'success',
          title: t('package_actions.return_completed'),
          description: t('package_actions.delivery_cancelled_returned')
        });
      } else {
        setDelivery(updatedDelivery);
        const otherParty = isCarrier ? t('package_actions.sender') : t('package_actions.carrier');
        
        addToast({
          type: 'info',
          title: t('package_actions.return_confirmed'),
          description: t('package_actions.waiting_for_other_party', { party: otherParty })
        });
      }

    } catch (error) {
      console.error('Error confirming return:', error);
      
      addToast({
        type: 'error',
        title: t('package_actions.error'),
        description: error.message || t('package_actions.error_return_confirmation')
      });
    } finally {
      setActionLoading(false);
    }
  };

  // Handle report user during return process
  const handleReportUserReturn = async () => {
    if (actionLoading) return;
    
    try {
      setActionLoading(true);
      setError(null);
      
      if (!delivery?.id) {
        throw new Error(t('package_actions.error_no_delivery'));
      }

      // Escalate to admin review when user reports during return process
      const { data: updatedDelivery, error: updateError } = await supabase
        .from('package_deliveries')
        .update({
          status: PackageStatusV2.WAITING_REVIEW,
          delivery_dispute_details: {
            ...delivery.delivery_dispute_details,
            return_process_disputed: true,
            reported_by: currentUser.id,
            reported_at: new Date().toISOString(),
            escalation_reason: 'user_reported_during_return'
          },
          updated_at: new Date().toISOString()
        })
        .eq('id', delivery.id)
        .select()
        .single();

      if (updateError) throw updateError;

      setDelivery(updatedDelivery);
      setLastRefresh(Date.now());

      // Notify other party about the report using database translation
      const otherUserId = currentUser.id === delivery.sender_id ? delivery.carrier_id : delivery.sender_id;
      try {
        const { error: notificationError } = await supabase.rpc('create_translated_notification', {
          target_user_id: otherUserId,
          notification_type: 'return_disputed',
          sender_id: currentUser.id,
          conversation_id: conversationId,
          related_id: delivery.id.toString()
        });
        
        if (notificationError) {
          console.error('Failed to create return dispute notification:', notificationError);
        }
      } catch (notificationError) {
        console.error('Error creating return dispute notification:', notificationError);
      }
      
      await addSystemMessage(t('package_actions.return_process_reported_system'));

      addToast({
        type: 'info',
        title: t('package_actions.report_submitted'),
        description: t('package_actions.admin_will_review_return')
      });

    } catch (error) {
      console.error('Error reporting user during return:', error);
      
      addToast({
        type: 'error',
        title: t('package_actions.error'),
        description: error.message || t('package_actions.error_report_user')
      });
    } finally {
      setActionLoading(false);
    }
  };

  // Check for expired disputes
  const checkDisputeTimeout = async () => {
    if (!delivery?.dispute_response_deadline) return;
    
    const deadline = new Date(delivery.dispute_response_deadline);
    const now = new Date();
    
    if (now > deadline && delivery.status === PackageStatusV2.DELIVERY_DISPUTED) {
      console.log('Dispute deadline expired, escalating to review');
      
      try {
        const { data: updatedDelivery, error: updateError } = await supabase
          .from('package_deliveries')
          .update({
            status: PackageStatusV2.WAITING_REVIEW,
            dispute_response_deadline: null,
            delivery_dispute_details: {
              ...delivery.delivery_dispute_details,
              timeout_escalated: true,
              escalated_at: new Date().toISOString(),
              escalation_reason: 'no_carrier_response'
            },
            updated_at: new Date().toISOString()
          })
          .eq('id', delivery.id)
          .select()
          .single();

        if (updateError) throw updateError;

        setDelivery(updatedDelivery);
        await addSystemMessage(t('package_actions.dispute_timeout_escalated'));
        
        // Notify both parties using database translation
        try {
          const { error: senderNotificationError } = await supabase.rpc('create_translated_notification', {
            target_user_id: delivery.sender_id,
            notification_type: 'dispute_escalated',
            sender_id: currentUser.id,
            conversation_id: conversationId,
            related_id: delivery.id.toString()
          });
          
          if (senderNotificationError) {
            console.error('Failed to create sender dispute timeout notification:', senderNotificationError);
          }
        } catch (notificationError) {
          console.error('Error creating sender dispute timeout notification:', notificationError);
        }
        
        try {
          const { error: carrierNotificationError } = await supabase.rpc('create_translated_notification', {
            target_user_id: delivery.carrier_id,
            notification_type: 'dispute_escalated',
            sender_id: currentUser.id,
            conversation_id: conversationId,
            related_id: delivery.id.toString()
          });
          
          if (carrierNotificationError) {
            console.error('Failed to create carrier dispute timeout notification:', carrierNotificationError);
          }
        } catch (notificationError) {
          console.error('Error creating carrier dispute timeout notification:', notificationError);
        }

      } catch (error) {
        console.error('Error handling dispute timeout:', error);
      }
    }
  };

  // Determine user roles
  const isPackageSender = currentUser?.id === packageData?.user_id;
  const isCarrier = delivery?.carrier_id === currentUser?.id;
  
  const getUserRole = () => {
    if (!currentUser || !packageData) return null;
    return isPackageSender ? t('package_actions.sender') : t('package_actions.carrier');
  };

  const getOtherUserRole = () => {
    const userRole = getUserRole();
    return userRole === t('package_actions.sender') ? t('package_actions.carrier') : t('package_actions.sender');
  };

  const userRole = getUserRole();
  const otherUserRole = getOtherUserRole();

  // Get status info for display
  const getStatusInfo = (status) => {
    let icon = <FaBox />;
    let color = 'var(--main-color)';
    let needsAction = false;
    let label = '';
    
    switch (status) {
      case PackageStatusV2.REQUEST_SENT:
        label = t('package_actions.delivery_request');
        color = 'var(--accent-color)';
        icon = <FaRegHandshake />;
        needsAction = isPackageSender;
        break;
      case PackageStatusV2.CONFIRMED:
        label = t('package_actions.confirmed'); 
        color = 'var(--accent-color)';
        icon = <FaCheckCircle />;
        needsAction = true;
        break;
      case PackageStatusV2.PICKUP_MEETING_AGREEMENT:
        label = t('package_actions.setting_pickup_meeting');
        color = '#F59E0B';
        icon = <FaClock />;
        needsAction = true;
        break;
      case PackageStatusV2.PICKUP_MEETING_AGREED:
        label = t('package_actions.pickup_agreed');
        color = 'var(--accent-color)';
        icon = <FaHandshake />;
        needsAction = true;
        break;
      case PackageStatusV2.PICKUP_ARRIVAL_PHASE:
        label = t('package_actions.pickup_arrival');
        color = '#F59E0B';
        icon = <FaMapMarkerAlt />;
        needsAction = true;
        break;
      case PackageStatusV2.PICKUP_WAITING:
        label = t('package_actions.pickup_waiting');
        color = '#F59E0B';
        icon = <FaClock />;
        needsAction = false;
        break;
      case PackageStatusV2.PICKUP_BOTH_PRESENT:
        label = t('package_actions.pickup_both_present');
        color = 'var(--accent-color)';
        icon = <FaUsers />;
        needsAction = isCarrier;
        break;
      case PackageStatusV2.PACKAGE_PICKED_UP:
        label = t('package_actions.package_picked_up');
        color = '#3B82F6';
        icon = <FaTruck />;
        needsAction = true;
        break;
      case PackageStatusV2.DELIVERY_MEETING_AGREEMENT:
        label = t('package_actions.setting_delivery_time');
        color = '#F59E0B';
        icon = <FaClock />;
        needsAction = true;
        break;
      case PackageStatusV2.DELIVERY_MEETING_AGREED:
        label = t('package_actions.delivery_countdown');
        color = 'var(--accent-color2)';
        icon = <FaTruck />;
        needsAction = true;
        break;
      case PackageStatusV2.DELIVERY_ARRIVAL_PHASE:
        label = t('package_actions.delivery_arrival');
        color = '#F59E0B';
        icon = <FaMapMarkerAlt />;
        needsAction = true;
        break;
      case PackageStatusV2.DELIVERY_WAITING:
        label = t('package_actions.delivery_waiting');
        color = '#F59E0B';
        icon = <FaClock />;
        needsAction = false;
        break;
      case PackageStatusV2.DELIVERY_BOTH_PRESENT:
        label = t('package_actions.delivery_both_present');
        color = 'var(--accent-color)';
        icon = <FaUsers />;
        needsAction = isCarrier;
        break;
      case PackageStatusV2.PACKAGE_DELIVERED:
        label = t('package_actions.package_delivered');
        color = '#8B5CF6';
        icon = <FaBox />;
        needsAction = isPackageSender;
        break;
      case PackageStatusV2.DELIVERY_CONFIRMED:
        label = t('package_actions.delivery_confirmed');
        color = 'var(--accent-color)';
        icon = <FaCheckCircle />;
        break;
      case PackageStatusV2.COMPLETED:
        label = t('package_actions.completed');
        color = 'var(--accent-color)';
        icon = <FaCheckCircle />;
        break;
      case PackageStatusV2.CANCELLED:
        label = t('package_actions.cancelled');
        color = 'var(--light-color)';
        icon = <FaTimes />;
        break;
      case PackageStatusV2.DECLINED:
        label = t('package_actions.declined');
        color = 'var(--light-color)';
        icon = <FaTimes />;
        break;
      case PackageStatusV2.PICKUP_SENDER_NO_SHOW:
        label = t('package_actions.sender_no_show');
        color = '#EF4444';
        icon = <FaExclamationTriangle />;
        break;
      case PackageStatusV2.PICKUP_CARRIER_NO_SHOW:
        label = t('package_actions.carrier_no_show');
        color = '#EF4444';
        icon = <FaExclamationTriangle />;
        break;
      case PackageStatusV2.DELIVERY_DISPUTED:
        label = t('package_actions.delivery_disputed');
        color = '#F59E0B';
        icon = <FaExclamationTriangle />;
        needsAction = isCarrier;
        break;
      case PackageStatusV2.WAITING_REVIEW:
        label = t('package_actions.waiting_review');
        color = '#8B5CF6';
        icon = <FaClock />;
        needsAction = false;
        break;
      case PackageStatusV2.RETURN_REQUESTED:
        label = t('package_actions.return_requested');
        color = '#F59E0B';
        icon = <FaHandshake />;
        needsAction = true;
        break;
      default:
        label = userRole || t('package_actions.package');
    }
    
    return { label, icon, color, needsAction };
  };

  // Get compact status label for button display
  const getCompactLabel = () => {
    if (loading) return t('app.loading');
    
    if (delivery) {
      const statusInfo = getStatusInfo(delivery.status);
      return statusInfo.label;
    }
    
    return userRole || t('package_actions.package');
  };

  // Render phase-specific components
  const renderPhaseComponent = () => {
    if (!delivery) return null;

    switch (delivery.status) {
      case PackageStatusV2.CONFIRMED:
      case PackageStatusV2.PICKUP_MEETING_AGREEMENT:
        return (
          <PickupMeetingAgreement
            delivery={delivery}
            currentUser={currentUser}
            otherUser={otherUser}
            userRole={userRole}
            packageData={packageData}
            onUpdate={(updatedDelivery) => {
              setDelivery(updatedDelivery);
              onUpdate && onUpdate(updatedDelivery);
            }}
            addSystemMessage={addSystemMessage}
            createNotification={createNotification}
          />
        );
      
      case PackageStatusV2.PICKUP_MEETING_AGREED:
        return (
          <PickupArrivalConfirmation
            delivery={delivery}
            currentUser={currentUser}
            otherUser={otherUser}
            userRole={userRole}
            packageData={packageData}
            onUpdate={(updatedDelivery) => {
              setDelivery(updatedDelivery);
              onUpdate && onUpdate(updatedDelivery);
            }}
            addSystemMessage={addSystemMessage}
            createNotification={createNotification}
          />
        );
      
      case PackageStatusV2.PICKUP_ARRIVAL_PHASE:
      case PackageStatusV2.PICKUP_WAITING:
      case PackageStatusV2.PICKUP_BOTH_PRESENT:
        return (
          <PickupWaitingStatus
            delivery={delivery}
            currentUser={currentUser}
            otherUser={otherUser}
            userRole={userRole}
            packageData={packageData}
            onUpdate={(updatedDelivery) => {
              setDelivery(updatedDelivery);
              setLastRefresh(Date.now());
              onUpdate && onUpdate(updatedDelivery);
            }}
            addSystemMessage={addSystemMessage}
            createNotification={createNotification}
          />
        );
      
      case PackageStatusV2.PACKAGE_PICKED_UP:
      case PackageStatusV2.DELIVERY_MEETING_AGREEMENT:
        return (
          <DeliveryMeetingAgreement
            delivery={delivery}
            currentUser={currentUser}
            otherUser={otherUser}
            userRole={userRole}
            packageData={packageData}
            onUpdate={(updatedDelivery) => {
              setDelivery(updatedDelivery);
              setLastRefresh(Date.now());
              onUpdate && onUpdate(updatedDelivery);
            }}
            addSystemMessage={addSystemMessage}
            createNotification={createNotification}
          />
        );
      
      case PackageStatusV2.DELIVERY_MEETING_AGREED:
        return (
          <DeliveryCountdownStatus
            delivery={delivery}
            currentUser={currentUser}
            otherUser={otherUser}
            userRole={userRole}
            packageData={packageData}
            onUpdate={(updatedDelivery) => {
              setDelivery(updatedDelivery);
              setLastRefresh(Date.now());
              onUpdate && onUpdate(updatedDelivery);
            }}
            addSystemMessage={addSystemMessage}
            createNotification={createNotification}
          />
        );
      
      case PackageStatusV2.DELIVERY_ARRIVAL_PHASE:
      case PackageStatusV2.DELIVERY_WAITING:
      case PackageStatusV2.DELIVERY_BOTH_PRESENT:
        return (
          <DeliveryWaitingStatus
            delivery={delivery}
            currentUser={currentUser}
            otherUser={otherUser}
            userRole={userRole}
            packageData={packageData}
            onUpdate={(updatedDelivery) => {
              setDelivery(updatedDelivery);
              setLastRefresh(Date.now());
              onUpdate && onUpdate(updatedDelivery);
            }}
            addSystemMessage={addSystemMessage}
            createNotification={createNotification}
          />
        );
      
      case PackageStatusV2.PACKAGE_DELIVERED:
        if (isPackageSender) {
          return (
            <div className="PAC-action-buttons-v2">
              <CustomButton10Co
                onClick={handleDeliveryConfirmation}
                label={t('package_actions.confirm_received')}
                disabled={actionLoading}
              >
                {actionLoading && <Spinner size="small" showText={false} thickness={2} />}
              </CustomButton10Co>
              <CustomButton10Ca
                onClick={handlePackageNotReceived}
                label={t('package_actions.not_received')}
                disabled={actionLoading}
              >
                {actionLoading && <Spinner size="small" showText={false} thickness={2} />}
              </CustomButton10Ca>
            </div>
          );
        } else {
          return <div style={{ fontSize: '11px', textAlign: 'center' }}>{t('package_actions.waiting_for_confirmation')}</div>;
        }
        
      case PackageStatusV2.DELIVERY_DISPUTED:
        if (isCarrier) {
          const deadline = delivery.dispute_response_deadline ? new Date(delivery.dispute_response_deadline) : null;
          const timeRemaining = deadline ? Math.max(0, deadline.getTime() - Date.now()) : 0;
          const hoursRemaining = Math.floor(timeRemaining / (1000 * 60 * 60));
          const minutesRemaining = Math.floor((timeRemaining % (1000 * 60 * 60)) / (1000 * 60));
          
          return (
            <div className="PAC-dispute-response">
              <div className="PAC-dispute-deadline">
                {timeRemaining > 0 ? (
                  <span style={{ fontSize: '11px', color: '#F59E0B' }}>
                    {t('package_actions.respond_within', { hours: hoursRemaining, minutes: minutesRemaining })}
                  </span>
                ) : (
                  <span style={{ fontSize: '11px', color: '#EF4444' }}>
                    {t('package_actions.deadline_expired')}
                  </span>
                )}
              </div>
              <div className="PAC-action-buttons-v2">
                <CustomButton10Co
                  onClick={() => handleDisputeResponse(true)}
                  label={t('package_actions.was_delivered')}
                  disabled={actionLoading}
                >
                  {actionLoading && <Spinner size="small" showText={false} thickness={2} />}
                </CustomButton10Co>
                <CustomButton10Ca
                  onClick={() => handleDisputeResponse(false)}
                  label={t('package_actions.not_delivered')}
                  disabled={actionLoading}
                >
                  {actionLoading && <Spinner size="small" showText={false} thickness={2} />}
                </CustomButton10Ca>
              </div>
            </div>
          );
        } else {
          return (
            <div style={{ fontSize: '11px', textAlign: 'center' }}>
              {t('package_actions.waiting_carrier_response')}
            </div>
          );
        }

      case PackageStatusV2.WAITING_REVIEW:
        return (
          <div className="PAC-waiting-review">
            <div style={{ fontSize: '11px', textAlign: 'center', color: '#8B5CF6' }}>
              {t('package_actions.admin_reviewing_case')}
            </div>
            <div style={{ fontSize: '10px', textAlign: 'center', color: 'var(--light-color)', marginTop: '5px' }}>
              {t('package_actions.admin_will_contact_soon')}
            </div>
          </div>
        );

      case PackageStatusV2.RETURN_REQUESTED:
        const isCarrierReturn = currentUser?.id === delivery.carrier_id;
        const hasUserConfirmed = isCarrierReturn ? delivery.return_confirmed_by_carrier : delivery.return_confirmed_by_sender;
        const hasOtherConfirmed = isCarrierReturn ? delivery.return_confirmed_by_sender : delivery.return_confirmed_by_carrier;
        
        return (
          <div className="PAC-return-process">
            {!hasUserConfirmed ? (
              <>
                <div style={{ fontSize: '11px', marginBottom: '10px', textAlign: 'center' }}>
                  {t('package_actions.coordinate_package_return')}
                </div>
                <div className="PAC-action-buttons-v2">
                  <CustomButton10Co
                    onClick={handleReturnConfirmation}
                    label={t('package_actions.confirm_return')}
                    disabled={actionLoading}
                  >
                    {actionLoading && <Spinner size="small" showText={false} thickness={2} />}
                  </CustomButton10Co>
                  <CustomButton10Ca
                    onClick={handleReportUserReturn}
                    label={t('package_actions.report_user')}
                    disabled={actionLoading}
                  >
                    {actionLoading && <Spinner size="small" showText={false} thickness={2} />}
                  </CustomButton10Ca>
                </div>
              </>
            ) : hasOtherConfirmed ? (
              <div style={{ fontSize: '11px', textAlign: 'center', color: 'var(--accent-color)' }}>
                {t('package_actions.return_completed_successfully')}
              </div>
            ) : (
              <div style={{ fontSize: '11px', textAlign: 'center' }}>
                {t('package_actions.waiting_other_party_confirm_return')}
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  // Render appropriate actions based on status and roles
  const renderActions = () => {
    if (loading) {
      return (
        <div className="PAC-loading">
          <Spinner size="small" showText={false} />
        </div>
      );
    }

    if (error) {
      return (
        <div className="PAC-error">
          <p>{error}</p>
        </div>
      );
    }

    // Handle completed deliveries
    if (delivery?.status === PackageStatusV2.COMPLETED) {
      const isSender = currentUser?.id === delivery.sender_id;
      const isCarrier = currentUser?.id === delivery.carrier_id;
      
      // Determine which review status to check based on user role
      let canReview = false;
      if (isSender) {
        canReview = !senderReviewSubmitted;
      } else if (isCarrier) {
        canReview = !carrierReviewSubmitted;
      }
      
      console.log('📦 Completed delivery review status:', {
        deliveryId: delivery.id,
        currentUserId: currentUser?.id,
        senderId: delivery.sender_id,
        carrierId: delivery.carrier_id,
        isSender,
        isCarrier,
        senderReviewSubmitted,
        carrierReviewSubmitted,
        canReview
      });
      
      return (
        <div className="PAC-action-buttons">
          <div style={{ fontSize: '11px' }}>{t('package_actions.delivery_completed')}</div>
          {canReview && (
            <CustomButton10Co
              onClick={() => {
                console.log('🔘 Review button clicked for delivery:', delivery.id);
                setShowReviewDialog(true);
              }}
              label={t('package_actions.leave_review')}
              disabled={actionLoading}
            >
              <FaRegStar />
            </CustomButton10Co>
          )}
        </div>
      );
    }
    
    // Handle cancelled/declined deliveries
    if (delivery?.status === PackageStatusV2.CANCELLED) {
      return (
        <div style={{ fontSize: '11px', textAlign: 'center', color: 'var(--light-color)' }}>
          {t('package_actions.delivery_cancelled')}
        </div>
      );
    }
    
    if (delivery?.status === PackageStatusV2.DECLINED) {
      return (
        <div style={{ fontSize: '11px', textAlign: 'center', color: 'var(--light-color)' }}>
          {t('package_actions.delivery_declined')}
        </div>
      );
    }
  
    // Handle no delivery scenario
    if (!delivery && !loading) {
      if (isPackageSender) {
        return <div style={{ fontSize: '11px', textAlign: 'center' }}>{t('package_actions.ask_carriers')}</div>;
      }
      
      return (
        <CustomButton10
          onClick={handleRequestDelivery}
          label={t('package_actions.offer_delivery')}
          disabled={actionLoading}
        >
          {actionLoading ? <Spinner size="small" showText={false} thickness={2} /> : <FaTruck />}
        </CustomButton10>
      );
    }
  
    // Handle delivery request responses
    if (delivery.status === PackageStatusV2.REQUEST_SENT) {
      return isPackageSender ? (
        <div className="PAC-action-buttons-v2">
          <CustomButton10Co 
            onClick={() => handleRequestResponse(true)}
            disabled={actionLoading}
          >
            {actionLoading ? <Spinner size="small" showText={false} thickness={2} /> : t('buttons.accept')}
          </CustomButton10Co>
          <CustomButton10Ca 
            onClick={() => handleRequestResponse(false)}
            disabled={actionLoading}
          >
            {actionLoading ? <Spinner size="small" showText={false} thickness={2} /> : t('buttons.decline')}
          </CustomButton10Ca>
        </div>
      ) : (
        <div style={{ fontSize: '11px', textAlign: 'center' }}>{t('package_actions.delivery_request_sent')}</div>
      );
    }

    // Render phase-specific components
    return renderPhaseComponent();
  };

  // Determine if we need to show the action indicator
  const showActionIndicator = !loading && delivery && getStatusInfo(delivery.status).needsAction;
  
  // Get status info for display
  const getDisplayStatusInfo = () => {
    if (loading) {
      return {
        label: t('app.loading'),
        icon: <FaClock />,
        color: 'var(--accent-color2)',
        needsAction: false
      };
    }
    
    if (delivery) {
      return getStatusInfo(delivery.status);
    }
    
    return {
      label: userRole || t('package_actions.package'),
      icon: <FaBox />,
      color: 'var(--main-color)',
      needsAction: false
    };
  };
  
  const statusInfo = getDisplayStatusInfo();

  return (
    <>
      <div className="PAC-package-actions-v2">
        <div className='PAC-package-actions-button-fr'>
          <button 
            className={`PAC-package-actions-button ${showActionIndicator ? 'PAC-needs-action' : ''}`}
            onClick={handleToggleDropdown}
            onDoubleClick={handleManualRefresh}
            aria-label={t('package_actions.toggle_actions')}
            aria-expanded={dropdownOpen}
            aria-haspopup="true"
            title={t('package_actions.double_click_to_refresh')}
          >
            <span className="PAC-package-status-icon" style={{ color: statusInfo.color }}>
              {statusInfo.icon}
            </span>
            <span className="PAC-package-status-text" style={{ color: statusInfo.color }}>
              {getCompactLabel()}
            </span>
            <FaCaretDown size={14} className="PAC-dropdown-icon" />
            {showActionIndicator && <div className="PAC-action-indicator"></div>}
            {manualRefreshing && <Spinner size="small" showText={false} thickness={1} />}
          </button>
        </div>
        
        {dropdownOpen && (
          <div className="PAC-package-actions-dropdown" ref={dropdownRef}>
            <div className="PAC-dropdown-header-v2">
              {loading ? (
                <div className="PAC-loading-status">
                  <Spinner size="small" showText={false} />
                  <span>{t('app.loading')}</span>
                </div>
              ) : (
                <PackageStatusDisplay 
                  delivery={delivery}
                  currentUser={currentUser}
                  otherUser={otherUser}
                  userRole={userRole}
                  otherUserRole={otherUserRole}
                  packageData={packageData}
                />
              )}
            </div>
            <div className="PAC-dropdown-content-v2">
              {renderActions()}
            </div>
          </div>
        )}
      </div>
      
      {showReviewDialog && ReactDOM.createPortal(
        <PackageReviewDialog 
          deliveryId={delivery.id}
          delivery={delivery}
          isSender={currentUser?.id === delivery.sender_id}
          onClose={() => setShowReviewDialog(false)}
          onReviewSubmitted={(isSender) => {
            if (isSender) {
              setSenderReviewSubmitted(true);
            } else {
              setCarrierReviewSubmitted(true);
            }
            // Force a re-render by updating state
            setLastRefresh(Date.now());
            addSystemMessage(t('package_actions.review_submitted_message'));
          }}
        />,
        document.body
      )}
    </>
  );
};

// PackageReviewDialog Component (separated for clarity)
const PackageReviewDialog = ({ 
  deliveryId, 
  delivery,
  isSender,
  onClose,
  onReviewSubmitted
}) => {
  const { t } = useTranslation('common');
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  const { addToast } = useToast();

  // Check if review already exists when dialog opens
  useEffect(() => {
    const checkExisting = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;
        
        const { data } = await supabase
          .from('package_reviews')
          .select('id')
          .eq('delivery_id', deliveryId)
          .eq('reviewer_id', user.id)
          .single();
          
        if (data) {
          setAlreadySubmitted(true);
          setError(t('package_actions.review_already_submitted'));
        }
      } catch (error) {
        // Review doesn't exist, which is expected
        console.log('No existing review found');
      }
    };
    
    checkExisting();
  }, [deliveryId, t]);

  const handleSubmitReview = async () => {
    if (!rating) {
      setError(t('package_actions.please_select_rating'));
      return;
    }
    
    if (submitting || alreadySubmitted) return;

    try {
      setSubmitting(true);
      setError(null);

      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) throw new Error(t('package_actions.authentication_error'));
      if (!user) throw new Error(t('package_actions.not_authenticated'));

      console.log('🗺 Review submission debug:', {
        currentUser: user.id,
        delivery: {
          id: deliveryId,
          sender_id: delivery.sender_id,
          carrier_id: delivery.carrier_id
        },
        isSender,
        rating
      });

      // Determine the correct reviewee based on the user's role
      const actualRevieweeId = user.id === delivery.sender_id ? delivery.carrier_id : delivery.sender_id;
      
      console.log('🎯 Reviewee calculation:', {
        userIsSender: user.id === delivery.sender_id,
        actualRevieweeId,
        expectedReviewee: isSender ? delivery.carrier_id : delivery.sender_id
      });

      // Verify we're not trying to review ourselves
      if (user.id === actualRevieweeId) {
        throw new Error(t('package_actions.cannot_review_yourself'));
      }

      const { data: reviewData, error: reviewError } = await supabase
        .from('package_reviews')
        .insert({
          delivery_id: deliveryId,
          reviewer_id: user.id,
          reviewee_id: actualRevieweeId,
          rating: parseInt(rating),
          comment: comment?.trim() || null,
          created_at: new Date().toISOString()
        })
        .select('*')
        .single();

      if (reviewError) {
        console.error('Review submission error:', reviewError);
        
        // Check if it's a duplicate review error
        if (reviewError.code === '23505' || reviewError.message?.includes('unique_delivery_reviewer')) {
          setAlreadySubmitted(true);
          throw new Error(t('package_actions.review_already_exists'));
        }
        
        throw new Error(reviewError.message);
      }

      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('username')
        .eq('id', user.id)
        .single();

      if (profileError) {
        console.error('Error fetching reviewer profile:', profileError);
      }

      const reviewerName = profileData?.username || t('package_actions.user');

      const { error: notificationError } = await supabase
        .from('notifications')
        .insert({
          user_id: actualRevieweeId,
          type: 'new_package_review',
          message: t('package_actions.notification_new_review', { name: reviewerName, rating: rating }),
          related_id: reviewData.id,
          sender_id: user.id,
          created_at: new Date().toISOString()
        });

      if (notificationError) {
        console.error('Notification error:', notificationError);
      }

      addToast({
        type: 'success',
        title: t('package_actions.review_submitted'),
        description: t('package_actions.review_submitted_description')
      });

      // Immediately update the state to prevent re-submission
      setAlreadySubmitted(true);
      
      // Notify parent component
      onReviewSubmitted(isSender);
      
      // Close dialog after short delay to show success state
      setTimeout(() => {
        onClose();
      }, 500);

    } catch (error) {
      console.error('Error submitting review:', error);
      setError(error.message || t('package_actions.failed_to_submit_review'));

      addToast({
        type: 'error',
        title: t('package_actions.error'),
        description: error.message || t('package_actions.could_not_submit_review')
      });
    } finally {
      setSubmitting(false);
    }
  };

  const renderStars = () => (
    <div className="PAC-star-rating">
      {[1, 2, 3, 4, 5].map((star) => (
        <span
          key={star}
          className={`PAC-star ${star <= rating ? 'PAC-selected' : ''}`}
          onClick={() => setRating(star)}
          onMouseEnter={() => setRating(star)}
          style={{ cursor: 'pointer' }}
          aria-label={t('package_actions.star_rating', { count: star })}
          role="button"
          tabIndex={0}
        >
          <FaRegStar />
        </span>
      ))}
    </div>
  );

  return (
    <div className="PAC-modal-overlay" onClick={submitting ? null : onClose}>
      <div className="PAC-modal2" onClick={(e) => e.stopPropagation()}>
        <h2>{t('package_actions.review_delivery')}</h2>
        {error && (
          <div className="PAC-error-message" style={{ color: 'var(--accent-color2)', marginBottom: '10px' }}>
            {error}
          </div>
        )}

        <div className="PAC-review-content">
          {renderStars()}
          
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder={t('package_actions.share_delivery_experience')}
            className="PAC-review-textarea"
            rows={4}
            maxLength={500}
            disabled={submitting}
            aria-label={t('package_actions.textarea_label')}
          />
        </div>

        <div className="PAC-modal-buttons">
          <CustomButton10Ca
            onClick={onClose}
            label={t('buttons.cancel')}
            disabled={submitting}
          />
          {!alreadySubmitted && (
            <CustomButton10Co
              onClick={handleSubmitReview}
              label={submitting ? t('buttons.submitting') : t('buttons.submit')}
              disabled={submitting || alreadySubmitted}
            >
              {submitting && <Spinner size="small" showText={false} thickness={2} />}
            </CustomButton10Co>
          )}
        </div>
      </div>
    </div>
  );
};

export default PackageActionsV2;